import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { IState } from 'angular-ui-router';
import { cloneDeep, find, includes, map as lodashMap, reduce, some } from 'lodash';
import { Observable, Observer, Subject, SubscriptionLike as ISubscription } from 'rxjs';
import {
  delayWhen,
  filter as rxjsFilter,
  flatMap,
  map as rxjsMap,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { ImStudentSupport } from 'Src/app/shared/static-models/im-models/im-student-support.service';
import { getStudentSupportsEntitiesList, getStudentSupportsLoadedStatus, LoadStudentSupports } from 'Src/ng2/store';
import { SpinnerModal } from '../../../../app/shared/modals/status-spinner/status-spinner.service';
import { RegentsSubject } from '../../constants/regents.constant';
import { StudentSupportStatuses } from '../../constants/student-support-statuses.constant';
import { IPaginatedResult } from '../../services/api-service/api-service';
import { UserRolePermissionsForModelService } from '../../services/user-role-permissions-for-model/user-role-permissions-for-model.service';
import { IStudentSupport } from '../../typings/interfaces/student-support.interface';
import { ISchoolSupport } from '../../typings/interfaces/support.interface';
import { BaseModalComponent } from '../base-modal.component';
import { IBaseModalData } from '../modals.service';
import { ImSupport } from './../../../../app/shared/static-models/im-models/im-support.service';
import { SupportCategories } from './../../../../ng2/shared/constants/support-categories.constant';
import { getCurrentUser } from './../../../store/selectors/current-user-selectors';
import { IFlag } from './../../typings/interfaces/flags.interface';
import { ASSIGN_SUPPORT_MODAL_CONFIG } from './assign-support-modal.config';
import { AssignSupportModalService } from './assign-support-modal.service';

export interface IAssignSupportModalComponentData extends IBaseModalData {
  schoolId: string;
  studentIds: string[];
  category?: ISchoolSupport['category'];
  examSubjectKey?: string; // Optional - it used when category === 'REGENTS_PREP`. Otherwise, it is ignored. (CM)
  status?: string;
  isEditMode?: boolean;
}

interface ISchoolSupportData {
  data: ISchoolSupport[];
}

/* istanbul ignore next */
@Component({
  selector: 'assign-support-modal',
  template: require('./assign-support-modal.component.html'),
  styles: [require('./assign-support-modal.component.scss')],
})
export class AssignSupportModalComponent extends BaseModalComponent implements OnInit, OnDestroy {
  public form: FormGroup;
  public patch: Partial<IFlag>;
  public supports: ISchoolSupport[];
  public countSupports: number;
  public queries: any;
  public placeholder: string;
  public placeholderIsEnabled: boolean = true;
  public currentSearch: string;
  public currentSearchDisplay: string;
  public optionIsSelected: boolean = false;
  public showAddSupportButton: boolean;
  public count: number;
  // Modal Configurations
  readonly dropdown = ASSIGN_SUPPORT_MODAL_CONFIG.dropdown;
  readonly buttons = ASSIGN_SUPPORT_MODAL_CONFIG.buttons;

  public destroy$: Subject<boolean> = new Subject<boolean>();
  public supportFound: boolean = true;

  dropdownSubscription: ISubscription;
  schoolSupportsSubscription: ISubscription;
  userPermissionSubscription: ISubscription;
  studentSupportsSubscription: ISubscription;

  public itemCount: number;
  public itemType: string;
  public title: string;
  public confirmationButtonLabel: string;
  public filteredSupports$: Observable<ISchoolSupport[]>;
  private studentSupportsEntities$: Observable<IStudentSupport[]>;
  public isEditMode: boolean;

  constructor(
    dialogRef: MatDialogRef<AssignSupportModalComponent, Partial<IStudentSupport>>,
    private formBuilder: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: IAssignSupportModalComponentData,
    @Inject('SpinnerModal') private spinnerModal: SpinnerModal,
    @Inject('Rollbar') private rollbar,
    @Inject('$state') private $state,
    @Inject('ImSupport') private imSupport: ImSupport,
    @Inject('ImStudentSupport') private imStudentSupport: ImStudentSupport,
    private assignSupportModalService: AssignSupportModalService,
    private store: Store<any>,
    private userRolePermissionsForModelService: UserRolePermissionsForModelService,
  ) {
    super(dialogRef);
  }

  public ngOnInit(): void {
    // clone data to avoid mutating the passed in data
    const { category, examSubjectKey, studentIds, isEditMode, status, isProfileMode } = (this.data = cloneDeep(
      this.data,
    ));
    this.itemCount = studentIds.length;
    this.itemType = 'student';
    this.isEditMode = isEditMode;
    this.isProfileMode = isProfileMode;

    // update modal UI
    if (isEditMode) {
      this.title = status === StudentSupportStatuses.backend.COMPLETED ? 'Mark complete' : 'Remove from support';
      this.confirmationButtonLabel = status === StudentSupportStatuses.backend.COMPLETED ? 'Complete' : 'Remove';
    } else {
      this.title = 'Assign support';
      this.confirmationButtonLabel = 'Assign';
    }
    // build form
    this.form = this.buildForm();

    // get placeholder value
    this.placeholder = this.getPlaceholder({ category, examSubjectKey });

    const valueChanges$ = this.form.get('dropdown').valueChanges;
    const schoolSupports$ = this.assignSupportModalService.getSupports(this.data);
    this.filteredSupports$ = this._setSupports(schoolSupports$, valueChanges$);

    // perform action when user interacts with autocomplete
    this._subscribeToDropdownValueChanges(this.form);

    // set permissioning // TODO: reenable once `create new support from dropdown` requirement is reintroduced
    this._setPermissioning();
  }

  goState(state: IState): void {
    super.close();
    this.spinnerModal.activateSpinner('Loading...');
    this.$state.go(state);
  }

  public close(): void {
    super.close();
  }

  public submit(form: FormGroup): void {
    this._processForm(form);
  }

  public _subscribeToDropdownValueChanges(form) {
    // Used to determine whether an option from the dropdown autocomplete is selected (CM)
    const dropdownController = form.controls.dropdown;

    this.dropdownSubscription = dropdownController.valueChanges.subscribe(value => {
      if (!value) this.supportFound = true;

      if (this.supports) {
        // NOTE: this only triggered on MOUSE selection
        this.optionIsSelected = some(this.supports, (support: any) => {
          return support._id === value;
        });
        if (this.optionIsSelected) {
          this.supportFound = true;
          this.placeholderIsEnabled = false;
        } else {
          this.supportFound = false;
          this.placeholderIsEnabled = true;
        }
      }
    });
  }

  private filterSupports(supports) {
    supports.data = supports.data.filter((support: ISchoolSupport) => {
      return !this.imSupport.isCompleted(support);
    });
    return supports;
  }

  private formatSupports({ data }): ISchoolSupport[] {
    return data.map((support: ISchoolSupport) => {
      support.subText = this.imSupport.getCategoryHumanName(support);
      const isAfter = moment().isBefore(support.schedule.startsOn);
      if (isAfter) {
        support.subText += ' | Starts on ' + moment(support.schedule.startsOn).format('MM/DD/YYYY');
      }
      return support;
    });
  }

  private sortSupports(supports): ISchoolSupport[] {
    return supports.sort((a, b) => {
      const aName = a.name.toLowerCase();
      const bName = b.name.toLowerCase();
      if (aName < bName) return -1;
      if (aName > bName) return 1;
      return 0;
    });
  }

  private filterValueChanges(value) {
    if (!value) this.supportFound = true;
    this.currentSearch = value;
    this.currentSearchDisplay = this.currentSearch ? `'${this.currentSearch}'` : '';
    return this.filter(value);
  }

  private _setSupports(
    supports$: Observable<IPaginatedResult<ISchoolSupport>>,
    valueChanges$: Observable<string>,
  ): Observable<ISchoolSupport[]> {
    this.schoolSupportsSubscription = supports$.subscribe(supports => (this.countSupports = supports.data.length));
    return supports$.pipe(
      take(1),
      rxjsFilter((supports: ISchoolSupportData) => this.filterSupports(supports)),
      rxjsMap((supports: ISchoolSupportData) => this.formatSupports(supports)),
      rxjsMap((supports: any) => this.sortSupports(supports)),
      tap((supports: ISchoolSupport[]) => (this.supports = supports)),
      switchMap(() => {
        return valueChanges$.pipe(
          startWith(''),
          rxjsMap((value: string) => this.filterValueChanges(value)),
        );
      }),
    );
  }

  public getPlaceholder(options: {
    category?: IAssignSupportModalComponentData['category'];
    examSubjectKey?: IAssignSupportModalComponentData['examSubjectKey'];
  }) {
    const { category, examSubjectKey } = options;
    let placeholder;

    if (category) {
      // TODO: add rollbar loging
      const categoryHumanName = SupportCategories[category] && SupportCategories[category].humanName.toLowerCase();

      if (examSubjectKey) {
        const { humanName } = find(RegentsSubject, subject => subject.key === examSubjectKey);
        placeholder = `Search ${humanName.toLowerCase()} ${categoryHumanName} supports...`;
      } else {
        placeholder = `Search ${categoryHumanName} supports...`;
      }
    } else {
      placeholder = 'Search all supports...';
    }

    return placeholder;
  }

  private filter(val: string) {
    if (val && this.supports) {
      this.placeholderIsEnabled = false;

      return this.supports.filter((support: ISchoolSupport) => {
        const lowerCasedVal = val.toLowerCase();
        const { metaData } = support;
        const nameFilter = support.name.toLowerCase().includes(lowerCasedVal);
        // categoryFilter applies to supports with a `category` of `REGENTS_PREP` (CM)
        const categoryFilter = this.imSupport
          .getCategoryHumanName(support)
          .toLowerCase()
          .includes(lowerCasedVal);
        // categoryFilter applies to supports with a `category` of `ACADEMIC` or `PHYS_HEALTH_SERVICES` (CM)
        const serviceNameFilter =
          metaData && metaData.serviceName && metaData.serviceName.toLowerCase().includes(lowerCasedVal);

        const filter = nameFilter || serviceNameFilter || categoryFilter;
        if (filter) this.supportFound = true;
        return filter;
      });
    }

    return this.supports;
  }

  public buildForm(): FormGroup {
    const dropdownInitialVal = undefined;
    const dropdownValidator = Validators.required;

    const form = this.formBuilder.group({
      dropdown: [dropdownInitialVal, dropdownValidator],
    });

    return form;
  }

  private _processAssignMode(selectedSupportId: string): void {
    const payload: ISchoolSupport = this.getSupportById(selectedSupportId);
    super.close(payload);
  }

  private _processEditMode(selectedSupportId: string): void {
    const payload = { studentSupportIds: [] };
    this.studentSupportsSubscription = this._getStudentSupports$(this.data.schoolId).subscribe(
      (studentSupports: IStudentSupport[]) => {
        const acc = { data: [] };
        const results = reduce(
          studentSupports,
          (acc, studentSupport: IStudentSupport) => {
            const { support } = studentSupport;
            const selectedSupportFoundInStudentSupport = support.supportId === selectedSupportId;
            if (selectedSupportFoundInStudentSupport) {
              const studentId = this.imStudentSupport.getStudentIdFrom_Id(studentSupport);
              const selectedStudentFoundInStudentSupports = includes(this.data.studentIds, studentId);
              if (selectedStudentFoundInStudentSupports) {
                acc.data.push(studentSupport);
                return acc;
              }
            }
            return acc;
          },
          acc,
        );
        payload.studentSupportIds = lodashMap(results.data, ss => ss._id);
        super.close(payload);
      },
    );
  }

  private _processForm(form: FormGroup): void {
    const value: { dropdown: ISchoolSupport['_id'] } = form.value;
    const { dropdown: supportId } = value;

    if (this.isEditMode) {
      this._processEditMode(supportId);
    } else {
      // Ideally, patch should be a StudentSupport - however, studentSupports currently live in the v2 store,
      // so the result is the selected support so that we can use `sdcActions#createStudentSupportsAsync` when
      // subscribing to the `afterClosed` event of this modal (CM).
      this._processAssignMode(supportId);
    }
  }

  // Used as a partial in template. Otherwise, context is lost (CM)
  public displayWith(value: ISchoolSupport['_id']) {
    const selectedSupport = value && this.getSupportById(value);

    return selectedSupport && selectedSupport.name;
  }

  private getSupportById(supportId: ISchoolSupport['_id']): ISchoolSupport {
    const support = this.supports && this.supports.find(({ _id }) => _id === supportId);

    return support;
  }

  // checks to see if user can add new school supports
  public _setPermissioning() {
    this.userPermissionSubscription = this.store
      .select(getCurrentUser)
      .pipe(take(1))
      .subscribe(user => {
        const SCHOOL_SUPPORTS_PERMISSIONING_MODEL_NAME = 'SUPPORT';
        const canCreateSchoolSupports = this.userRolePermissionsForModelService.canCreatePartial(
          SCHOOL_SUPPORTS_PERMISSIONING_MODEL_NAME,
        );
        const obj = null; // we are not checking permissioning for an instance, but for the MODEL as a whole

        this.showAddSupportButton = canCreateSchoolSupports(obj, user);
      });
  }

  public setPlaceholder() {
    if (this.supportFound) this.placeholderIsEnabled = false;
  }

  public clearSelection(): void {
    setTimeout(() => {
      /**
       * Using `form.reset()` for some reason automatically triggers the filtering
       * function again, and does so using the last selected field as the new search term.
       * This results in the menu opening with just last selected field displayed in the dropdown options.
       * Manually deleting that does return the full menu.
       *
       * So instead of resetting form, just pass an empty string to ensure
       * that the full menu is returned - JYR
       */
      this.form.controls.dropdown.setValue('');
      this.optionIsSelected = false;
      this.supportFound = true;
      this.placeholderIsEnabled = true;
    }, 0);
  }

  private _getStudentSupports$(schoolId): Observable<IStudentSupport[]> {
    return this.store.select(getStudentSupportsLoadedStatus).pipe(
      delayWhen((loaded: boolean) => this._loadStudentSupports$(loaded, schoolId)),
      flatMap(() => this.store.select(getStudentSupportsEntitiesList)),
      take(1),
    );
  }

  private _loadStudentSupports$(loaded: boolean, schoolId): Observable<any> {
    return Observable.create(async (observer: Observer<any>) => {
      if (!loaded) {
        this.store.dispatch(new LoadStudentSupports({ schoolId }));
      } else {
        observer.next('user loaded');
        observer.complete();
      }
    });
  }
}
